import React, { useState, useEffect, useMemo } from 'react';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/* ── Animated Building Component ── */
function Building({ x, width, height, delay, variant, glowing }) {
  const windowRows = Math.floor(height / 18);
  const windowCols = Math.max(2, Math.floor(width / 14));

  return (
    <g className={`building ${glowing ? 'building--glow' : ''}`} style={{ animationDelay: `${delay}s` }}>
      {/* Building body */}
      <rect
        x={x} y={300 - height} width={width} height={height}
        rx={2} ry={2}
        className={`building__body building__body--${variant}`}
      />
      {/* Rooftop accent */}
      <rect
        x={x + 2} y={300 - height - 3} width={width - 4} height={3}
        rx={1}
        className="building__roof"
      />
      {/* Windows */}
      {Array.from({ length: windowRows }).map((_, row) =>
        Array.from({ length: windowCols }).map((_, col) => {
          const lit = Math.random() > 0.35;
          const wx = x + 5 + col * (width - 10) / windowCols;
          const wy = (300 - height) + 12 + row * 18;
          return (
            <rect
              key={`${row}-${col}`}
              x={wx} y={wy} width={6} height={8} rx={1}
              className={`building__window ${lit ? 'building__window--lit' : ''}`}
              style={{ animationDelay: `${delay + 0.8 + Math.random() * 2}s` }}
            />
          );
        })
      )}
    </g>
  );
}

/* ── Floating Particles ── */
function Particles({ count = 30 }) {
  const particles = useMemo(() =>
    Array.from({ length: count }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 2.5,
      duration: 8 + Math.random() * 15,
      delay: Math.random() * 10,
      opacity: 0.15 + Math.random() * 0.4,
    })), [count]
  );

  return (
    <div className="login-particles" aria-hidden="true">
      {particles.map(p => (
        <div
          key={p.id}
          className="login-particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: p.opacity,
          }}
        />
      ))}
    </div>
  );
}

/* ── Skyline SVG ── */
function Skyline({ glowing }) {
  const buildings = [
    { x: 0,   w: 45, h: 120, d: 0.1, v: 'a' },
    { x: 50,  w: 35, h: 85,  d: 0.25, v: 'b' },
    { x: 90,  w: 55, h: 180, d: 0.05, v: 'a' },
    { x: 150, w: 40, h: 140, d: 0.2, v: 'c' },
    { x: 195, w: 60, h: 200, d: 0.0, v: 'a' },
    { x: 260, w: 38, h: 110, d: 0.3, v: 'b' },
    { x: 302, w: 50, h: 165, d: 0.1, v: 'c' },
    { x: 358, w: 42, h: 95,  d: 0.35, v: 'b' },
    { x: 405, w: 65, h: 220, d: 0.0, v: 'a' },
    { x: 475, w: 35, h: 130, d: 0.15, v: 'c' },
    { x: 515, w: 48, h: 155, d: 0.08, v: 'a' },
    { x: 568, w: 40, h: 90,  d: 0.28, v: 'b' },
    { x: 612, w: 55, h: 185, d: 0.04, v: 'c' },
    { x: 672, w: 38, h: 105, d: 0.22, v: 'b' },
    { x: 715, w: 50, h: 170, d: 0.12, v: 'a' },
    { x: 770, w: 45, h: 125, d: 0.18, v: 'c' },
  ];

  return (
    <div className={`login-skyline ${glowing ? 'login-skyline--glow' : ''}`}>
      <svg viewBox="0 0 820 300" preserveAspectRatio="xMidYMax slice" className="login-skyline__svg">
        <defs>
          <linearGradient id="skyGlow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00a87e" stopOpacity="0" />
            <stop offset="100%" stopColor="#00a87e" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="buildingA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <linearGradient id="buildingB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a2332" />
            <stop offset="100%" stopColor="#0c1220" />
          </linearGradient>
          <linearGradient id="buildingC" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#162030" />
            <stop offset="100%" stopColor="#0a0f1a" />
          </linearGradient>
        </defs>
        {/* Ground reflection */}
        <rect x="0" y="297" width="820" height="3" fill="url(#skyGlow)" />
        {buildings.map((b, i) => (
          <Building
            key={i} x={b.x} width={b.w} height={b.h}
            delay={b.d} variant={b.v} glowing={glowing}
          />
        ))}
      </svg>
    </div>
  );
}

/* ── Login Page ── */
export default function Login() {
  const [phase, setPhase] = useState('idle'); // idle | authenticating | redirecting
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleLogin = async () => {
    setPhase('authenticating');

    try {
      const res = await fetch(`${API_URL}/auth/login`);
      const data = await res.json();
      const url = data.auth_url || '';

      if (url.startsWith('https://login.microsoftonline.com/') || url.startsWith('https://login.microsoft.com/')) {
        setPhase('redirecting');
        // Kurze Verzögerung damit die Animation sichtbar wird
        setTimeout(() => { window.location.href = url; }, 600);
      } else {
        console.error('Unerwartete Login-URL:', url);
        setPhase('idle');
      }
    } catch (err) {
      console.error('Login-Fehler:', err);
      setPhase('idle');
    }
  };

  return (
    <div className={`login2 ${mounted ? 'login2--mounted' : ''} login2--${phase}`}>
      {/* Ambient background */}
      <div className="login2__bg">
        <div className="login2__gradient" />
        <div className="login2__stars" />
      </div>

      {/* Particle system */}
      <Particles count={35} />

      {/* Skyline */}
      <Skyline glowing={phase !== 'idle'} />

      {/* Login Card */}
      <div className="login2__card-wrap">
        <div className={`login2__card ${phase !== 'idle' ? 'login2__card--active' : ''}`}>

          <div className="login2__logo">
            <img src="/assets/logos/one-immo-lockup-on-dark.svg" alt="One. immo" />
          </div>

          {/* Divider with glow */}
          <div className="login2__divider" />

          {/* Body */}
          {phase === 'idle' && (
            <div className="login2__body">
              <h1>Willkommen</h1>
              <p>Melden Sie sich an, um Ihre Immobilien-Plattform zu nutzen.</p>

              <button className="login2__btn" onClick={handleLogin}>
                <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                <span>Mit Microsoft anmelden</span>
              </button>
            </div>
          )}

          {/* Authenticating state */}
          {phase !== 'idle' && (
            <div className="login2__loading">
              <div className="login2__spinner">
                <div className="login2__spinner-building">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path d="M8 36V14l12-8 12 8v22H8z" stroke="#00a87e" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M16 36v-8h8v8" stroke="#00a87e" strokeWidth="1.5" strokeLinejoin="round" />
                    <rect x="14" y="18" width="4" height="4" rx=".5" fill="#00a87e" opacity=".5" />
                    <rect x="22" y="18" width="4" height="4" rx=".5" fill="#00a87e" opacity=".5" />
                    <rect x="14" y="25" width="4" height="4" rx=".5" fill="#00a87e" opacity=".3" />
                    <rect x="22" y="25" width="4" height="4" rx=".5" fill="#00a87e" opacity=".3" />
                  </svg>
                </div>
                <div className="login2__spinner-ring" />
              </div>
              <p className="login2__loading-text">
                {phase === 'authenticating' ? 'Verbindung wird hergestellt...' : 'Weiterleitung zu Microsoft...'}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="login2__footer">
            One Dot Immo GmbH
          </div>
        </div>
      </div>
    </div>
  );
}
